// CreateEventDialog — modal form for creating a new event.
// Events don't have ownership roles — they use recorded_by (the creating user).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { eventsApi, eventTypesApi, type CreateEventRequest } from '@/lib/api/events'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'

interface CreateEventDialogProps {
  categoryId: string
  categoryName: string
  open: boolean
  onClose: () => void
  onCreated: (eventId: string) => void
}

export function CreateEventDialog({
  categoryId,
  categoryName,
  open,
  onClose,
  onCreated,
}: CreateEventDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const evtApi = eventsApi(api)
  const typeApi = eventTypesApi(api)

  // Fetch event types for the dropdown
  const { data: eventTypes } = useQuery({
    queryKey: queryKeys.events.types(slug),
    queryFn: () => typeApi.list(),
    enabled: open,
  })

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [eventTypeId, setEventTypeId] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Pre-select first "incident" type when types load
  const defaultTypeId =
    eventTypes?.find((t) => t.type_name.toLowerCase() === 'incident')?.id ??
    eventTypes?.[0]?.id ??
    ''

  const selectedTypeId = eventTypeId || defaultTypeId

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error('Event title is required')
      if (!selectedTypeId) throw new Error('Event type is required')

      const data: CreateEventRequest = {
        title: title.trim(),
        description: description.trim() || undefined,
        event_type_id: selectedTypeId,
        category_id: categoryId,
        event_date: eventDate || undefined,
      }

      return evtApi.create(data)
    },
    onSuccess: (evt) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'event') })
      resetForm()
      onCreated(evt.id)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function resetForm() {
    setTitle('')
    setDescription('')
    setEventTypeId('')
    setEventDate('')
    setError(null)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Record Event</h2>
            <p className="text-sm text-muted-foreground">in {categoryName}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="evt-title">Event Title *</Label>
            <Input
              id="evt-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Server room flooding"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evt-desc">Description</Label>
            <textarea
              id="evt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the event"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evt-type">Event Type *</Label>
            <select
              id="evt-type"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={selectedTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
            >
              {!eventTypes && <option value="">Loading...</option>}
              {eventTypes?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.type_name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evt-date">Event Date</Label>
            <Input
              id="evt-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !title.trim() || !selectedTypeId}
          >
            {createMutation.isPending ? 'Creating...' : 'Record Event'}
          </Button>
        </div>
      </div>
    </div>
  )
}
